import React, { useState } from "react";
import Link from "next/link";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import Tooltip from "@JohnLewisPartnership/jl-design-system/dist/components/tooltip/Tooltip";
import Form from "@JohnLewisPartnership/checkout-forms/dist/components/form-simple/Form";
import Input from "@JohnLewisPartnership/jl-design-system/dist/elements/input/Input";
import { excludeSymbolsNames } from "@JohnLewisPartnership/checkout-forms/dist/form/normalizers/restrictInput";
import { STRICT_EMAIL } from "@JohnLewisPartnership/checkout-forms/dist/form/fields/email";
import styles from "./TrackReturnAndRefund.module.scss";
import Cookies from 'js-cookie';
import uuid from "uuid/v1";
import auth0 from '@/utils/auth0';
import createAxiosClient from "../../../../clients/axiosClient";

const ORDER_NUMBER = {
  component: Input,
  id: "orderNumber",
  name: "orderNumber",
  label: "Order number",
  labelHelperClassName: styles.labelHelperClassName,
  labelHelperElement: (
    <>
      <div>Where to find this</div>
      <Tooltip id="orderNumberTooltip" popoverClassname="tooltipPopover">
        <span>You can find your order number in your confirmation email</span>
      </Tooltip>
    </>
  ),
  type: "text",
  placeholder: "Please enter your order number",
  className: "fieldLarge",
  hint: null,
  required: true,
  maxLength: 10,
  regex: /^[0-9]{8,10}$/,
  validationMessageKeys: {
    required: "Please enter your order number",
    regex: "Please enter an 8-10 digit order number",
  },
  normalize: excludeSymbolsNames,
};

export const ORDER_EMAIL = {
  ...STRICT_EMAIL,
  id: "orderEmail",
  name: "orderEmail",
  label: "Email address",
  placeholder: "Please enter your email address",
  className: "fieldLarge",
  validationMessageKeys: {
    ...STRICT_EMAIL.validationMessageKeys,
    required: "Please enter the email you used while placing the order",
  },
};

function TrackReturnAndRefund() {
  const [errorMsg, setErrorMsg] = useState(null);
  const handleSubmit = async ({ orderNumber, orderEmail }, onComplete) => {
    try {
      setErrorMsg(null);
      const email = btoa(orderEmail);
      const basePath =
        process.env.NEXT_PUBLIC_ENVIRONMENT_NAME === "stubs" ? "/api/mock" : process.env.NEXT_PUBLIC_API_DOMAIN;

      const token = await auth0()?.getTokenIfAuthenticated();

      // Get or create CSRF token
      let csrfToken = Cookies.get("jlmyaccount_csrf");
      if (!csrfToken) {
        csrfToken = uuid();
        Cookies.set("jlmyaccount_csrf", csrfToken, {
          secure: true,
          domain: `.${document.domain.split(".").slice(1).join(".")}`,
        });
      }

      const headers = {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const helpUIAxiosClient = await createAxiosClient({
        headers,
        withCredentials: true,
      });
      const url = `${basePath}/self-serve/orders/bff/vmo/v2/view-my-order/${orderNumber}`;
      const res = await helpUIAxiosClient.post(url, {
        email
      });

      if (res.status === 200) {
        window.location.assign(`${process.env.NEXT_PUBLIC_ACCOUNT_DOMAIN}/orders/${orderNumber}#${email}`);
      }
    } catch (err) {
      const errorResponse = err.response?.data;
      if (errorResponse.status === 400 || errorResponse.status === 404)  {
        const code = errorResponse.code;
        if (
          code === "auth.invalid.request" ||
          code === "unauthorized" ||
          code === "resource.not.found"
        ) {
          setErrorMsg("We can't match your record. Please check your order number and order email");
        }
      } else if (err.status === 500 || errorResponse.status === 500) {
        setErrorMsg("Sorry, we can't display the page you requested");
      } else if (errorResponse.message) {
        setErrorMsg(errorResponse.message);
      } else {
        setErrorMsg("Something went wrong");
      }
    } finally {
      onComplete();
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <h4 className={styles.title}>Track return and refund</h4>
        <div>
          <div className={styles.trackContainer}>
            <div className={styles.signInSection}>
              <h3>Sign in to track a return</h3>
              <PrimaryButton tag="a" href={`${process.env.NEXT_PUBLIC_ACCOUNT_DOMAIN}/orders`} className={styles.button}>
                Sign in
              </PrimaryButton>
            </div>
            <h3>Or find your order</h3>
            <Form
              formId="trackOrderForm"
              fields={[ORDER_NUMBER, ORDER_EMAIL]}
              onSubmit={handleSubmit}
              submitButton="Continue"
              submitting={true}
              hideFirstErrorLink={!!errorMsg}
              serverError={errorMsg ? { title: errorMsg } : null}
            />
          </div>
          <Message
            className={styles.infoBox}
            type="information"
            title="Issues with tracking a return and refund?"
            body={
              <span>
                To find out how to track a return and check refund timelines, visit{" "}
                <Link href="https://www.johnlewis.com/customer-services/returns/how-to-return-your-item" target="_blank" rel="noopener noreferrer">
                  our returns page
                </Link>
                .
              </span>
            }
            whiteBackground
          />
        </div>
      </div>
    </div>
  );
}

export default TrackReturnAndRefund;